import { Check, Circle } from 'lucide-react';
import { PASSWORD_REQUIREMENTS, getPasswordStrength } from '../../lib/passwordStrength';

interface PasswordRequirementsProps {
  password: string;
  /** 'dark' = glassmorphism signup card; 'light' = extended flow's white theme. */
  variant: 'dark' | 'light';
}

/**
 * Live password checklist + strength meter shared by every signup flow.
 * Rules come from PASSWORD_REQUIREMENTS so the UI can never drift from
 * validation.
 */
export default function PasswordRequirements({ password, variant }: PasswordRequirementsProps) {
  const strength = getPasswordStrength(password);
  const dark = variant === 'dark';

  return (
    <div className="space-y-2.5">
      {/* Strength meter */}
      <div className="flex items-center gap-2">
        <div className={`flex-1 flex gap-1 ${dark ? '' : ''}`}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${dark ? 'bg-white/10' : 'bg-gray-200'}`}
              style={i < strength.score ? { backgroundColor: strength.color } : undefined}
            />
          ))}
        </div>
        {password && (
          <span className="text-[11px] font-semibold" style={{ color: strength.color }}>
            {strength.label}
          </span>
        )}
      </div>

      {/* Requirement checklist */}
      <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
        {PASSWORD_REQUIREMENTS.map((req) => {
          const met = req.test(password);
          return (
            <li key={req.id} className="flex items-center gap-1.5">
              {met ? (
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-green-500 shrink-0">
                  <Check className="h-2.5 w-2.5 text-white" />
                </span>
              ) : (
                <Circle className={`h-3.5 w-3.5 shrink-0 ${dark ? 'text-gray-600' : 'text-gray-300'}`} />
              )}
              <span
                className={`text-[11px] ${
                  met
                    ? dark ? 'text-gray-300' : 'text-gray-600'
                    : dark ? 'text-gray-500' : 'text-gray-400'
                }`}
              >
                {req.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
